import type { IFolderKnowledgeFile } from "@frontend-shared/types";
import { EFileUploadStatus } from "@frontend-shared/types";
import React, { memo, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  useDeleteFolderKnowledgeFile,
  useGetFolderKnowledgeFiles,
  useUploadFolderKnowledgeFile,
} from "@/api/knowledge";
import { DeleteConfirmationDialog } from "@/components/DeleteConfirmationDialog";
import { KnowledgeFileUpload } from "@/pages/knowledge/components/KnowledgeFileUpload";

import { FolderKnowledgeFileRow } from "./FolderKnowledgeFileRow";
import { FolderKnowledgeUploadRow } from "./FolderKnowledgeUploadRow";

interface IFolderKnowledgePanelProps {
  folderUuid: string;
}

const FolderKnowledgePanelInner: React.FC<IFolderKnowledgePanelProps> = ({
  folderUuid,
}) => {
  const { t } = useTranslation("sidebar");

  const { data: files, isLoading } = useGetFolderKnowledgeFiles({ folderUuid });
  const folderFiles = files ?? [];
  const { uploads, uploadFile, removeUpload } =
    useUploadFolderKnowledgeFile(folderUuid);
  const { mutateAsync: deleteFile, isPending: isDeleting } =
    useDeleteFolderKnowledgeFile(folderUuid);

  const [fileToDelete, setFileToDelete] = useState<IFolderKnowledgeFile | null>(
    null,
  );

  const hasActiveUploads = useMemo(
    () => uploads.some((entry) => entry.status === EFileUploadStatus.UPLOADING),
    [uploads],
  );

  // NOTE: Only surface in-flight and failed uploads inline; completed uploads
  // appear in the refetched file list below.
  const pendingUploads = useMemo(
    () =>
      uploads.filter(
        (entry) =>
          entry.status === EFileUploadStatus.UPLOADING ||
          entry.status === EFileUploadStatus.ERROR,
      ),
    [uploads],
  );

  const handleUpload = useCallback(
    (selectedFiles: File[]): void => {
      // NOTE: NSAI-7817 — the hook queues them; at most two upload at once.
      for (const file of selectedFiles) {
        uploadFile(file);
      }
    },
    [uploadFile],
  );

  const handleConfirmDelete = useCallback((): void => {
    if (!fileToDelete) {
      return;
    }
    deleteFile(fileToDelete.uuid)
      .then(() => setFileToDelete(null))
      .catch(() => {
        // NOTE: Keep the confirmation open so the user can retry.
      });
  }, [deleteFile, fileToDelete]);

  return (
    <div className="space-y-3" data-testid="folder-knowledge-panel">
      <p className="text-sm font-medium">
        {t("chat_folder_list.folder_settings.knowledge_title")}
      </p>

      <KnowledgeFileUpload
        onUpload={handleUpload}
        isUploading={hasActiveUploads}
      />

      {pendingUploads.map((entry) => (
        <FolderKnowledgeUploadRow
          key={entry.id}
          entry={entry}
          onDismiss={removeUpload}
        />
      ))}

      {isLoading && (
        <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
          {t("chat_folder_list.folder_settings.knowledge_loading")}
        </div>
      )}
      {!isLoading && folderFiles.length > 0 && (
        <ul className="space-y-1">
          {folderFiles.map((file) => (
            <FolderKnowledgeFileRow
              key={file.uuid}
              file={file}
              onDelete={setFileToDelete}
            />
          ))}
        </ul>
      )}
      {!isLoading && folderFiles.length === 0 && (
        <p className="py-4 text-center text-sm text-muted-foreground">
          {t("chat_folder_list.folder_settings.knowledge_empty")}
        </p>
      )}

      <DeleteConfirmationDialog
        open={fileToDelete !== null}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            setFileToDelete(null);
          }
        }}
        isDeleting={isDeleting}
        testIdPrefix="folder-knowledge-file"
        content={{
          step1: {
            title: t("chat_folder_list.folder_settings.delete_file_title"),
            description: t(
              "chat_folder_list.folder_settings.delete_file_description",
              { name: fileToDelete?.name ?? "" },
            ),
          },
        }}
        handlers={{
          onStep1Submit: handleConfirmDelete,
        }}
      />
    </div>
  );
};

export const FolderKnowledgePanel = memo(FolderKnowledgePanelInner);
